/**
 * Theme Context for Dark Mode Support
 * Follows system preference by default, with manual override option
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext(null);

// Storage key for localStorage
const THEME_STORAGE_KEY = 'kursadmin-theme';

// Get system preference
const getSystemTheme = () => {
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
};

// Get initial mode (system, light, or dark)
const getInitialMode = () => {
  const storedMode = localStorage.getItem(THEME_STORAGE_KEY);
  if (storedMode === 'dark' || storedMode === 'light') {
    return storedMode;
  }
  return 'system'; // Default to following system
};

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(getInitialMode);
  const [theme, setTheme] = useState(() => {
    const initialMode = getInitialMode();
    return initialMode === 'system' ? getSystemTheme() : initialMode;
  });

  // Update document attribute when theme changes
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e) => {
      // Only update if we're in system mode
      if (mode === 'system') {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    // Older browsers
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [mode]);

  // Cycle through modes: system -> light -> dark -> system
  const toggleTheme = useCallback(() => {
    setMode((currentMode) => {
      let newMode;
      if (currentMode === 'system') {
        newMode = 'light';
      } else if (currentMode === 'light') {
        newMode = 'dark';
      } else {
        newMode = 'system';
      }

      if (newMode === 'system') {
        localStorage.removeItem(THEME_STORAGE_KEY);
        setTheme(getSystemTheme());
      } else {
        localStorage.setItem(THEME_STORAGE_KEY, newMode);
        setTheme(newMode);
      }

      return newMode;
    });
  }, []);

  // Set specific mode
  const setThemeMode = useCallback((newMode) => {
    if (newMode === 'dark' || newMode === 'light') {
      localStorage.setItem(THEME_STORAGE_KEY, newMode);
      setMode(newMode);
      setTheme(newMode);
    } else if (newMode === 'system') {
      localStorage.removeItem(THEME_STORAGE_KEY);
      setMode('system');
      setTheme(getSystemTheme());
    }
  }, []);

  const isDarkMode = theme === 'dark';
  const isSystemMode = mode === 'system';

  return (
    <ThemeContext.Provider value={{ theme, mode, toggleTheme, setThemeMode, isDarkMode, isSystemMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Hook to access theme context
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
